import { useEffect, useState } from "react";
import LazyImage from "@/my_components/Common/LazyImage";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import { getTrendingCollection } from "@/services/courseCollectionService";
import { ROUTE } from "@/route/app_routes.js";
import CourseHoverTooltip, { useCourseHoverTooltip } from "@/my_components/Common/CourseHoverTooltip";

const SKELETON_COUNT = 5;

/**
 * The curated "Trending Courses" list (course_collections, collection_type = 'trending') --
 * admin-curated and stats-auto-curated, capped at 15 (see the backend's CourseCollectionCurator
 * and CourseCollectionController). Converted to a Swiper carousel (same setup/pattern as
 * InstructorsShowcase's layout="carousel" mode) since 15 cards no longer fit in a single static
 * row the way the previous ~5-course row did -- a carousel lets visitors click through all 15
 * instead of only ever seeing a handful.
 */
const TrendingCourses = () => {
  const { t } = useTranslation("home");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { tooltip, show, hide, cancelHide } = useCourseHoverTooltip();

  useEffect(() => {
    getTrendingCollection()
      .then((res) => setCourses(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rbt-trending">
        <div className="rbt-trending__row">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div className="rbt-trending__card" key={i}>
              <div className="rbt-skel" style={{ aspectRatio: "16/10", borderRadius: 14 }} />
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 14, marginTop: 10 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (courses.length === 0) return null;

  return (
    <Swiper
      className="rbt-trending-carousel event-activation-1 rbt-arrow-between rbt-dot-bottom-center pb--60"
      slidesPerView={2}
      spaceBetween={20}
      modules={[Navigation, Pagination]}
      pagination={{ el: ".rbt-trending-carousel-pagination", clickable: true }}
      navigation={{ nextEl: ".rbt-trending-arrow-right", prevEl: ".rbt-trending-arrow-left" }}
      breakpoints={{
        481: { slidesPerView: 2 },
        768: { slidesPerView: 3 },
        1200: { slidesPerView: 5 },
      }}
    >
      {courses.map((course, i) => (
        <SwiperSlide key={course.id}>
          <Link
            className="rbt-trending__card"
            href={`${ROUTE.course_details}${course.id}`}
            onMouseEnter={(e) => show(course, e.currentTarget)}
            onMouseLeave={hide}
          >
            <div className="rbt-trending__card-media">
              <LazyImage src={course.course_image_small} alt={course.title} fill sizes="(max-width: 991px) 45vw, 20vw" style={{ objectFit: "cover" }} />
              <span className="rbt-trending__card-rank">#{i + 1}</span>
            </div>
            <span className="rbt-trending__card-instructor">{course.instructor || "SmartTech Academy"}</span>
            <h3 className="rbt-trending__card-title">{course.title}</h3>
            <span className="rbt-trending__card-meta">
              <i className="feather-users"></i>{course.students} {t("trendingCourses.enrolledLabel", "enrolled")}
            </span>
          </Link>
        </SwiperSlide>
      ))}

      <div className="rbt-swiper-arrow rbt-arrow-left rbt-trending-arrow-left">
        <div className="custom-overfolow">
          <i className="rbt-icon feather-arrow-left"></i>
          <i className="rbt-icon-top feather-arrow-left"></i>
        </div>
      </div>

      <div className="rbt-swiper-arrow rbt-arrow-right rbt-trending-arrow-right">
        <div className="custom-overfolow">
          <i className="rbt-icon feather-arrow-right"></i>
          <i className="rbt-icon-top feather-arrow-right"></i>
        </div>
      </div>

      <div className="rbt-trending-carousel-pagination rbt-swiper-pagination" style={{ bottom: "0" }}></div>

      <CourseHoverTooltip tooltip={tooltip} onMouseEnter={cancelHide} onMouseLeave={hide} />
    </Swiper>
  );
};

export default TrendingCourses;
